import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import pluralize from 'pluralize';
import cx from 'classnames';
import queryString from 'query-string';
import moment, { Moment } from 'moment';
import { RangeValue } from 'rc-picker/lib/interface';
import { Button, DatePicker, Table } from 'antd';
import { ColumnsType } from 'antd/es/table/interface';
import {
  FilterValue,
  SorterResult,
  TablePaginationConfig,
} from 'antd/lib/table/interface';
import { useDidUpdate } from 'rooks';

import {
  TrendBillingByDatesSortFieldEnum,
  TrendBillingByDatesPagedResponse,
  TrendBillingItemByDatesViewModel,
} from 'api/backofficeApiClient';
import {
  getWeeklyReportData,
  getWeeklyReportExcel,
} from 'redux/reducers/homeDashboardSlice';
import { SortingDirectionEnum } from 'enums';
import { DateParams } from 'utils/filterUtils';
import { getDateWithoutTime } from 'utils/dateUtils';
import { downloadFileByObjectURL } from 'utils/fileUtils';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import { getSortOrderRequestValue } from 'utils/sortUtils';
import BasePageLabel from 'components/wrappers/BasePageLabel';
import BaseAlert from 'components/wrappers/BaseAlert/BaseAlert';
import useSortColumns, { ISortParams } from 'hooks/useSortColumns';
import LastSyncDateSection from 'components/LastSyncDateSection';
import BaseTableHeader from 'components/wrappers/BaseTableHeader';
import { useAppDispatch } from 'hooks/useAppDispatch';

import styles from './WeeklyTrendBillingReportDetailsPage.module.less';
import commonStyles from '../UserHomePage.module.less';

const pageSize = 8;

enum WeeklyReportFilterTypes {
  Date = 'date',
}

type WeeklyReportFilters = {
  [WeeklyReportFilterTypes.Date]: DateParams | undefined;
};

const columns: ColumnsType<TrendBillingItemByDatesViewModel> = [
  {
    title: 'Workweek Ending',
    key: TrendBillingByDatesSortFieldEnum.Date,
    render: (record: TrendBillingItemByDatesViewModel) => record.date,
  },
  {
    title: 'Billing $',
    key: TrendBillingByDatesSortFieldEnum.Billing,
    render: (record: TrendBillingItemByDatesViewModel) => record.billing,
  },
  {
    title: 'Profit $',
    key: TrendBillingByDatesSortFieldEnum.Profit,
    render: (record: TrendBillingItemByDatesViewModel) => record.profit,
  },
  {
    title: 'Profit %',
    key: TrendBillingByDatesSortFieldEnum.ProfitPercentage,
    render: (record: TrendBillingItemByDatesViewModel) =>
      record.profitPercentage,
  },
];

const defaultSortParams: ISortParams<TrendBillingByDatesSortFieldEnum> = {
  sortOrder: 'descend',
  sortBy: TrendBillingByDatesSortFieldEnum.Billing,
};

export default function WeeklyTrendBillingReportDetailsPage() {
  const [isInitialDataLoaded, setIsInitialDataLoaded] = useState(false);
  const [isWeeklyReportDataLoading, setIsWeeklyReportDataLoading] =
    useState(false);
  const [weeklyReportData, setWeeklyReportData] =
    useState<TrendBillingByDatesPagedResponse>();
  const [currentTablePage, setCurrentTablePage] = useState(1);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { sortBy, sortOrder, startDate, endDate } = queryString.parse(
    location.search,
  );
  const initialFilters = {
    [WeeklyReportFilterTypes.Date]:
      startDate && endDate
        ? {
            startDate: moment(startDate, 'MM/DD/YYYY'),
            endDate: moment(endDate, 'MM/DD/YYYY'),
          }
        : undefined,
  };
  const [filters, setFilters] = useState<WeeklyReportFilters>(initialFilters);
  const initialSortParams: ISortParams<TrendBillingByDatesSortFieldEnum> =
    sortBy && sortOrder
      ? {
          sortBy: parseInt(sortBy),
          sortOrder,
        }
      : defaultSortParams;

  const weeklyReportCountMessage = () => {
    const firstWord = pluralize('week', weeklyReportData?.totalCount, true);
    const secondWord = pluralize('record', weeklyReportData?.totalCount, false);
    return firstWord + '/' + secondWord;
  };

  const { sortColumns, sortParams, setSortParams } = useSortColumns(
    columns,
    initialSortParams,
  );

  useDidUpdate(() => {
    const queryData = {
      sortBy: sortParams.sortBy,
      sortOrder: sortParams.sortOrder,
      startDate: filters[WeeklyReportFilterTypes.Date]?.startDate
        ? filters[WeeklyReportFilterTypes.Date]?.startDate?.format('MM/DD/YYYY')
        : undefined,
      endDate: filters[WeeklyReportFilterTypes.Date]?.endDate
        ? filters[WeeklyReportFilterTypes.Date]?.endDate?.format('MM/DD/YYYY')
        : undefined,
    };
    navigate(`${location.pathname}?${queryString.stringify(queryData)}`, {
      replace: true,
    });
    // eslint-disable-next-line
  }, [sortParams.sortBy, sortParams.sortOrder, filters]);

  const loadWeeklyReportData = useCallback(async () => {
    setIsWeeklyReportDataLoading(true);

    const result = await dispatch(
      getWeeklyReportData({
        pageNumber: currentTablePage - 1,
        sortField: sortParams.sortBy,
        sortType: getSortOrderRequestValue(sortParams.sortOrder),
        startDate: filters[WeeklyReportFilterTypes.Date]?.startDate
          ? getDateWithoutTime(filters[WeeklyReportFilterTypes.Date]?.startDate)
          : undefined,
        endDate: filters[WeeklyReportFilterTypes.Date]?.endDate
          ? getDateWithoutTime(filters[WeeklyReportFilterTypes.Date]?.endDate)
          : undefined,
        pageSize: pageSize,
      }),
    );

    if (getWeeklyReportData.fulfilled.match(result)) {
      setWeeklyReportData(result.payload);
    } else {
      setWeeklyReportData({
        totalCount: 0,
        details: [],
      });
    }

    setIsWeeklyReportDataLoading(false);
  }, [
    dispatch,
    currentTablePage,
    sortParams.sortOrder,
    sortParams.sortBy,
    filters,
  ]);

  const loadInitialData = useCallback(async () => {
    await loadWeeklyReportData();

    setIsInitialDataLoaded(true);
  }, [loadWeeklyReportData]);

  const handleDownloadReport = async () => {
    const result = await dispatch(
      getWeeklyReportExcel({
        pageNumber: currentTablePage - 1,
        sortField: sortParams.sortBy,
        sortType: getSortOrderRequestValue(sortParams.sortOrder),
        startDate: filters[WeeklyReportFilterTypes.Date]?.startDate
          ? getDateWithoutTime(filters[WeeklyReportFilterTypes.Date]?.startDate)
          : undefined,
        endDate: filters[WeeklyReportFilterTypes.Date]?.endDate
          ? getDateWithoutTime(filters[WeeklyReportFilterTypes.Date]?.endDate)
          : undefined,
        pageSize: pageSize,
      }),
    );

    if (getWeeklyReportExcel.fulfilled.match(result)) {
      downloadFileByObjectURL(result.payload.url, result.payload.fileName);
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filter: Record<string, FilterValue | null>,
    sort:
      | SorterResult<TrendBillingItemByDatesViewModel>
      | SorterResult<TrendBillingItemByDatesViewModel>[],
  ) => {
    const sortOrder =
      (sort as SorterResult<TrendBillingItemByDatesViewModel>).order ||
      SortingDirectionEnum.Ascend;
    const sortBy = (sort as SorterResult<TrendBillingItemByDatesViewModel>)
      .columnKey as TrendBillingByDatesSortFieldEnum;

    setSortParams(({ sortBy: prevSortBy, sortOrder: prevSortOrder }) => {
      if (prevSortBy !== sortBy || prevSortOrder !== sortOrder) {
        // Reset table page if sorting has changed
        setCurrentTablePage(1);
      } else {
        setCurrentTablePage(pagination.current || 1);
      }

      return {
        sortOrder,
        sortBy,
      };
    });
  };

  const handleSelectDate =
    (type: WeeklyReportFilterTypes) => (date: RangeValue<Moment>) => {
      if (date) {
        setFilters({
          ...filters,
          [type]: {
            startDate: date[0],
            endDate: date[1],
          },
        });
      } else {
        setFilters({
          ...filters,
          [type]: undefined,
        });
      }
      setCurrentTablePage(1);
    };

  return (
    <div className="page-wrapper scroll-box">
      <div className="page-holder">
        <BasePageLabel label="Dashboard details">
          {weeklyReportData?.lastSyncDate && (
            <div className="title-line__info">
              <LastSyncDateSection
                syncDate={weeklyReportData.lastSyncDate}
                title="Weekly trend billing & profit report is automatically refreshed every 15 minutes."
              />
            </div>
          )}
        </BasePageLabel>
        <div className="page-box page-inner">
          <div className="page-inner-container">
            <BaseTableHeader
              title="Weekly Trend-Billing & Profit"
              enableSubTitle={isInitialDataLoaded}
              subTitle={weeklyReportCountMessage()}
            >
              <div>
                <DatePicker.RangePicker
                  key="filterByDate"
                  format="MM/DD/YYYY"
                  separator="-"
                  className="d-flex"
                  value={
                    filters[WeeklyReportFilterTypes.Date]
                      ? [
                          moment(
                            filters[WeeklyReportFilterTypes.Date]?.startDate,
                          ),
                          moment(
                            filters[WeeklyReportFilterTypes.Date]?.endDate,
                          ),
                        ]
                      : undefined
                  }
                  onChange={handleSelectDate(WeeklyReportFilterTypes.Date)}
                />
              </div>
              <div>
                <Button
                  className={commonStyles.saveBtn}
                  onClick={handleDownloadReport}
                  icon={<span className="icon icon-xls-file" />}
                />
              </div>
            </BaseTableHeader>
            <Table
              className={cx(styles.table, 'custom-table')}
              pagination={{
                total: weeklyReportData?.totalCount,
                pageSize: pageSize,
                current: currentTablePage,
                showSizeChanger: false,
              }}
              showSorterTooltip={false}
              onChange={handleTableChange}
              columns={sortColumns}
              dataSource={weeklyReportData?.details}
              rowKey={(record) => record?.date as string}
              loading={isWeeklyReportDataLoading}
              scroll={{ x: '100%' }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
